import React from 'react';
import Modal from 'components/layout/modal';
import PropTypes from 'prop-types';
import {FormattedMessage, injectIntl} from 'react-intl';
import Row from 'components/Row';
import Col from 'components/Col';
import EditableTextField from 'components/forms/editableTextField';
import EditableSelectField from 'components/forms/editableSelectField';
import {chemicalBaseTypeNames} from 'helpers/constants';
import moment from 'moment';
import EditableTextArea from 'components/forms/editableTextArea';
import CustomerModel from 'models/customer';
import PoolModel from 'models/pool';
import AnalysisModel from 'models/analysis';
import WaterTesterModel from 'models/waterTester';
import NoteModel from 'models/note';
import ValidateGroup from 'helpers/validation';

export const noteTypes = {
  customer: 'notes.type.account',
  system: 'notes.type.system',
  analysis: 'notes.type.analysis',
};

class NoteModal extends React.Component {
  static propTypes = {
    closeModal: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
    customer: PropTypes.instanceOf(CustomerModel).isRequired,
    pool: PropTypes.instanceOf(PoolModel),
    analysis: PropTypes.instanceOf(AnalysisModel),
    pools: PropTypes.arrayOf(PoolModel),
    analyses: PropTypes.arrayOf(AnalysisModel),
    waterTesters: PropTypes.arrayOf(WaterTesterModel),
    note: PropTypes.instanceOf(NoteModel),
  };

  constructor(props) {
    super(props);

    let type = 'customer';

    if (props.analysis) {
      type = 'analysis';
    } else if (props.pool) {
      type = 'system';
    }

    const note = props.note || {};

    this.state = {
      name: note.name || '',
      author: note.author || (props.waterTesters && props.waterTesters.length > 0 ? props.waterTesters[0].name : ''),
      body: note.body || '',
      type,
      selectedPool: props.pool && props.pool.id ? props.pool.id : undefined,
      selectedAnalysis: props.analysis && props.analysis.id ? props.analysis.id : undefined,
    };

    this.group = new ValidateGroup(this._save);
  }

  update = ({key, value}) => {
    if(key === 'type') {
      let {selectedPool, selectedAnalysis} = this.state;
      const {pools, analyses} = this.props;

      if(value === 'analysis') {
        selectedPool = undefined;
        selectedAnalysis = analyses && analyses.length > 0 ? analyses[0].id : undefined;
      }else if(value === 'system') {
        selectedAnalysis = undefined;
        selectedPool = pools && pools.length > 0 ? pools[0].id : undefined;
      }else {
        selectedPool = undefined;
        selectedAnalysis = undefined;
      }
      this.setState({[key]: value, selectedAnalysis, selectedPool});
    }else{
      this.setState({[key]: value});
    }
  };

  _save = () => {
    const {name, body, author, type, selectedPool, selectedAnalysis} = this.state;

    this.props.save({
      id: this.props.note && this.props.note.id ? this.props.note.id : undefined,
      name,
      author,
      type,
      body,
      selectedPool,
      selectedAnalysis,
    });
  };

  render() {
    const {intl, pools, analyses, waterTesters} = this.props;
    const {formatMessage} = intl;
    let {name, author, type, selectedPool, selectedAnalysis, body} = this.state;
    const getPoolSystemTypeNameFromAnalysis = (poolID) => {
      if(pools && pools.length > 0) {
        const pool = pools.find(x => x.id === poolID);

        return pool ? `${chemicalBaseTypeNames(pool.typeID, pool.chemicalBaseTypeID, formatMessage)} - ` : '';
      }
      return '';
    };

    analyses.sort((a, b) => moment(a.created).isBefore(moment(b.created)) ? 1 : -1);

    const isSaveNoteValid = type === 'system' && pools && pools.length > 0 || // sytem note and there are pools
    type === 'analysis' && analyses && analyses.length > 0 || // analysis note and there analyses
    type === 'customer';
    const isUpdatingNote = !!(this.props.note && this.props.note.id);

    return <Modal
      onClose={this.props.closeModal}
      headerText={<h2 className='notes__modal__title'>
        <FormattedMessage id={this.props.note && this.props.note.id ?
          'notes.edit' :
          'notes.add'} />
      </h2>}>
      <h3 className='notes__modal__date'>{moment().format('LL')}</h3>
      <form onSubmit={this.group.validate}>
        <Row>
          <Col md={4}>
            <EditableTextField dataKey='name'
                               value={name}
                               editMode={true}
                               onChange={this.update}
                               group={this.group}
                               required
                               placeholder={formatMessage({id: 'notes.placeholder.name'})} />
          </Col>

          <Col md={4}>
            <EditableSelectField dataKey='author'
                                 editMode={true}
                                 value={author}
                                 group={this.group}
                                 required
                                 validate={value => value !== formatMessage({id: 'timeline.notes.select.author'})}
                                 onChange={this.update}
                                 keys={waterTesters.map(x => x.name)}
                                 options={waterTesters.map(x => x.name)}/>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <EditableSelectField dataKey='type'
                                 editMode={true}
                                 value={type}
                                 group={this.group}
                                 onChange={this.update}
                                 keys={['customer', 'system', 'analysis']}
                                 options={[noteTypes.customer, noteTypes.system, noteTypes.analysis].map(x => <FormattedMessage key={x} id={x}/>)}
                                 disabled={isUpdatingNote}/>
          </Col>

          {type === 'system' ? <Col md={4}>
            <EditableSelectField editMode={true} dataKey={'selectedPool'} value={selectedPool} onChange={this.update}
                                 keys={pools.map(x => x.id)}
                                 group={this.group}
                                 options={pools.map(x => `${chemicalBaseTypeNames(x.typeID, x.chemicalBaseTypeID, formatMessage)} ${
                                   x.nickName ? ` | ${x.nickName}` : ''
                                 }`)}/>
          </Col> : null}


          {type === 'analysis' ? <Col md={4}>
            <EditableSelectField editMode={true} dataKey={'selectedAnalysis'} value={selectedAnalysis} onChange={this.update}
                                 keys={analyses.map(x => x.id)}
                                 group={this.group}
                                 options={analyses.map(x => `${getPoolSystemTypeNameFromAnalysis(x.poolID)} ${moment(x.created).format('ll - LT')}`)}/>
          </Col> : null}

        </Row>
        <Row>
          <Col md={12}>
            <EditableTextArea dataKey='body'
                              rows={4}
                              editMode={true}
                              value={body}
                              onChange={this.update}
                              group={this.group}
                              required
                              className='notes__field__body'
                              placeholder={formatMessage({id: 'notes.placeholder.details'})} />
          </Col>
        </Row>

        <Row>
          <Col md={6} mdOffset={6}>
            <div className='notes__modal__footer'>
              <button className='btn btn-default' onClick={this.props.closeModal}>
                <FormattedMessage id='general.cancel'/>
              </button>
              <button className='btn btn-primary' type='submit' disabled={!isSaveNoteValid}>
                <FormattedMessage id='general.save' />
              </button>
            </div>
          </Col>
        </Row>
      </form>
    </Modal>;
  }
}

export default injectIntl(NoteModal);
